/**
 * The production adapters for the chat session's ports (`model/chatSession`): the generated REST
 * client, the query cache as the server's word, the browser's media pipeline and the real clock.
 * Each is a thin translation; every rule about what to do with them is the session's.
 */

import type { QueryClient } from '@tanstack/react-query';

import { attentionQueueQueryKey } from '~/entities/client';
import {
  postChatConversationsConversationIdMessages,
  postChatConversationsConversationIdMessagesExerciseCard,
  postChatConversationsConversationIdMessagesExerciseClipRequest,
  postChatConversationsConversationIdMessagesImage,
  postChatConversationsConversationIdMessagesPlanCard,
  postChatConversationsConversationIdMessagesVideoSlot,
  postChatConversationsConversationIdMessagesVoiceSlot,
  postChatConversationsConversationIdMessagesWorkoutSummaryCard,
  postChatConversationsConversationIdRead,
} from '~/shared/api/generated/endpoints/chat/chat';
import { postMediaIdConfirm } from '~/shared/api/generated/endpoints/media/media';
import type {
  TrainWithMeModulesChatDtosResponsesConversationResponse as ConversationResponse,
} from '~/shared/api/generated/model';
import { contentLanguage } from '~/shared/i18n/contentLanguage';
import { encodeVoiceNote, VOICE_CLASS } from '~/shared/media/audio/encodeVoiceNote';
import { prepareCover } from '~/shared/media/compressCover';
import { prepareVideo } from '~/shared/media/video/prepareVideo';
import { putToSlot } from '~/shared/media/video/uploadVideo';
import { videoRefusalKey } from '~/shared/media/video/videoFile';

import { chatRoot, conversationsQueryKey, threadRoot } from '../api/chatQueries';
import type { ChatApi, ChatPorts, Clock, MediaPort, ServerTruth, SlotAnswer } from '../model/chatSession';
import { withConversationRead, withMessage, type ThreadData } from '../model/threadCache';

import { signalRHub } from './chatRealtime';

function slotAnswer(answer: { message: SlotAnswer['message']; upload?: SlotAnswer['upload'] }): SlotAnswer {
  return { message: answer.message, upload: answer.upload ?? null };
}

export const httpChatApi: ChatApi = {
  postText: (conversationId, clientId, content) =>
    postChatConversationsConversationIdMessages(conversationId, { clientId, content }),
  postImage: (conversationId, clientId, file, caption) =>
    postChatConversationsConversationIdMessagesImage(conversationId, {
      ClientId: clientId,
      File: file,
      ...(caption === '' ? {} : { Content: caption }),
    }),
  postCard(conversationId, clientId, choice) {
    switch (choice.kind) {
      case 'exercise':
        return postChatConversationsConversationIdMessagesExerciseCard(conversationId, {
          clientId,
          exerciseId: choice.exerciseId,
        });
      case 'clipRequest':
        // The ask names its exercise in the reader's language (exercise-clip-timeline R20).
        return postChatConversationsConversationIdMessagesExerciseClipRequest(
          conversationId,
          { clientId, exerciseId: choice.exerciseId },
          { lang: contentLanguage() },
        );
      case 'plan':
        return postChatConversationsConversationIdMessagesPlanCard(conversationId, {
          clientId,
          planAssignmentId: choice.planAssignmentId,
        });
      case 'summary':
        return postChatConversationsConversationIdMessagesWorkoutSummaryCard(conversationId, {
          clientId,
          workoutExecutionId: choice.workoutExecutionId,
        });
    }
  },
  videoSlot: async (conversationId, request) =>
    slotAnswer(
      await postChatConversationsConversationIdMessagesVideoSlot(conversationId, {
        clientId: request.clientId,
        contentType: 'video/mp4',
        sizeBytes: request.sizeBytes,
        durationSeconds: request.durationSeconds,
        ...(request.caption === '' ? {} : { content: request.caption }),
      }),
    ),
  voiceSlot: async (conversationId, request) =>
    slotAnswer(
      await postChatConversationsConversationIdMessagesVoiceSlot(conversationId, {
        clientId: request.clientId,
        contentType: VOICE_CLASS.contentType,
        sizeBytes: request.sizeBytes,
        durationSeconds: request.durationSeconds,
        waveform: request.waveform,
      }),
    ),
  confirmMedia: async (mediaId) => {
    await postMediaIdConfirm(mediaId);
  },
  markRead: async (conversationId, lastMessageId) => {
    await postChatConversationsConversationIdRead(conversationId, { lastMessageId });
  },
};

export function queryTruth(queryClient: QueryClient): ServerTruth {
  return {
    put(message) {
      const conversationId = message.conversationId;
      if (typeof conversationId !== 'number') return;
      queryClient.setQueriesData<ThreadData>({ queryKey: threadRoot(conversationId) }, (data) =>
        withMessage(data, message),
      );
    },
    reread(scope) {
      const queryKey =
        scope === 'inbox' ? conversationsQueryKey : scope === 'everything' ? chatRoot : threadRoot(scope.thread);
      void queryClient.invalidateQueries({ queryKey });
    },
    markedRead(conversationId) {
      queryClient.setQueryData<ConversationResponse[]>(conversationsQueryKey, (rows) =>
        withConversationRead(rows, conversationId),
      );
      // Reading the thread resolves its „Nieprzeczytana wiadomość" task on the server.
      void queryClient.invalidateQueries({ queryKey: attentionQueueQueryKey });
    },
  };
}

export const browserMedia: MediaPort = {
  async prepareImage(file) {
    const prepared = await prepareCover(file);
    return prepared.ok
      ? { ok: true, value: prepared.file }
      : { ok: false, notice: { key: `web:chat.image.${prepared.refusal}` } };
  },
  async prepareVideo(file, onProgress, signal) {
    const prepared = await prepareVideo(file, { onProgress, signal });
    return prepared.ok
      ? { ok: true, value: { file: prepared.file, durationSeconds: prepared.durationSeconds } }
      : { ok: false, notice: { key: videoRefusalKey(prepared.refusal) } };
  },
  async encodeVoice(recording) {
    const encoded = await encodeVoiceNote(recording);
    return encoded.ok ? { ok: true, value: encoded.file } : { ok: false, notice: { key: 'web:chat.voice.encodeFailed' } };
  },
  put: (upload, file, onProgress, signal) => putToSlot(upload, file, onProgress, signal),
  preview: (file) => URL.createObjectURL(file),
  revoke: (url) => {
    URL.revokeObjectURL(url);
  },
};

export const systemClock: Clock = {
  now: () => Date.now(),
  after(ms, run) {
    const timer = setTimeout(run, ms);
    return () => {
      clearTimeout(timer);
    };
  },
};

export function productionPorts(queryClient: QueryClient): ChatPorts {
  return {
    api: httpChatApi,
    hub: signalRHub(),
    truth: queryTruth(queryClient),
    media: browserMedia,
    clock: systemClock,
    newClientId: () => crypto.randomUUID(),
  };
}
